import Box from '@mui/material/Box';
import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JournalDetailSchema, JournalListRowSchema, type JournalDetailDto } from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { getData, getPage, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { recordPath } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { useUrlList } from '../../shared/ui/url-list';
import { ApprovalPanel, workflowHistoryKey } from '../../shared/workflow/ApprovalPanel';
import { refreshAfterPosting } from '../finance/cache';
import {
  DimensionRef,
  Facts,
  RecordFailure,
  RecordLink,
  useDimensionNames,
} from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { SearchFilterBar } from '../../shared/ui/filters';

const JOURNALS = '/api/v1/ledger/journals';
const JOURNAL = 'ledger.journal';
const label = (value: string) => value.replace(/_/g, ' ');

/**
 * Journals (FRD-FIN-039 to FIN-049; SDD §8.6, §8.9): every journal — posted and draft, from every
 * source — filtered on the server by status, source, kind, approval status, dates and a search of
 * number, reference and description, with the filters in the URL. An authorised user prepares a
 * manual journal from here.
 */
export function JournalsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, `${JOURNAL}:create`);
  const list = useUrlList({ pageSize: 50 });
  const params = list.apiParams(
    {
      search: 'search',
      status: 'status',
      sourceType: 'source_type',
      kind: 'journal_kind',
      approval: 'approval_status',
      from: 'from',
      to: 'to',
    },
    '-posting_date',
  );
  const journals = useQuery({
    queryKey: ['journals', params.toString()],
    queryFn: () => getPage(`${JOURNALS}?${params.toString()}`, JournalListRowSchema),
  });
  const select = (
    text: string,
    key: string,
    options: readonly [string, string][],
    minWidth = 170,
  ) => (
    <TextField
      select
      size="small"
      label={text}
      value={list.get(key)}
      sx={{ minWidth }}
      onChange={(e) => {
        list.set(key, e.target.value);
      }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map(([value, name]) => (
        <MenuItem key={value} value={value}>
          {name}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Journals"
        description="Every journal in the general ledger, posted and draft, with the transaction it came from."
        actions={
          canCreate ? (
            <Button
              component={RouterLink}
              to="/journals/new"
              variant="contained"
              startIcon={<AddIcon />}
            >
              New journal
            </Button>
          ) : undefined
        }
      />
      <Box role="search" aria-label="Filter the journals">
        <SearchFilterBar
          label="Search number, reference or description"
          value={list.get('search')}
          onChange={(value) => {
            list.set('search', value);
          }}
        >
          {select('Status', 'status', [
            ['draft', 'Draft'],
            ['posted', 'Posted'],
          ])}
          {select('Source', 'sourceType', [
            ['manual', 'Manual'],
            ['sub_ledger', 'Sub-ledger'],
            ['import', 'Import'],
            ['opening_balance', 'Opening balance'],
          ])}
          {select('Kind', 'kind', [
            ['standard', 'Standard'],
            ['reversal', 'Reversal'],
            ['adjustment', 'Adjustment'],
            ['controlled_adjustment', 'Controlled adjustment'],
          ])}
          {select('Approval', 'approval', [
            ['not_submitted', 'Not submitted'],
            ['submitted', 'Awaiting approval'],
            ['approved', 'Approved'],
            ['returned', 'Returned'],
            ['rejected', 'Rejected'],
          ])}
          <TextField
            size="small"
            type="date"
            label="From"
            value={list.get('from')}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              list.set('from', e.target.value);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={list.get('to')}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              list.set('to', e.target.value);
            }}
          />
        </SearchFilterBar>
      </Box>
      <Section>
        {journals.isPending && <LoadingState label="Loading journals" />}
        {journals.isError && <ErrorState message={journals.error.message} />}
        {journals.data?.items.length === 0 && <EmptyState title="No journals found" />}
        {journals.data !== undefined && journals.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Journals">
              <TableHead>
                <TableRow>
                  <TableCell>Journal</TableCell>
                  <TableCell>Posting date</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journals.data.items.map((journal) => (
                  <TableRow key={journal.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/journals/${journal.id}`}>{journal.documentNo}</RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{journal.postingDate}</TableCell>
                    <TableCell>{journal.accountingPeriodCode}</TableCell>
                    <TableCell>{label(journal.sourceType)}</TableCell>
                    <TableCell>{label(journal.journalKind)}</TableCell>
                    <TableCell>{journal.reference ?? '—'}</TableCell>
                    <TableCell align="right">{journal.totalDebit}</TableCell>
                    <TableCell align="right">{journal.totalCredit}</TableCell>
                    <TableCell>
                      <StatusChip status={journal.status} />
                      {journal.status === 'draft' && journal.approvalStatus !== 'not_submitted' && (
                        <StatusChip status={journal.approvalStatus} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={journals.data.total}
              page={list.page}
              rowsPerPage={list.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => {
                list.setPage(next);
              }}
              onRowsPerPageChange={(e) => {
                list.setPageSize(Number.parseInt(e.target.value, 10));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

/**
 * One journal (`/journals/:id`): header, source transaction, the journals it reverses or that
 * reverse it, its balanced lines with every dimension, and — for a manual journal — the actions
 * its state and the session's permissions allow: Edit and Submit on a draft, Post once approved
 * (or directly where no approval route is configured), Reverse on a posted manual journal. Its
 * approval history, supporting documents and audit trail follow (FRD-FIN-046, FIN-047, FIN-055).
 */
export function JournalDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const names = useDimensionNames();
  const can = (capability: string) => hasCapability(session.data, capability);
  const [dialog, setDialog] = useState<'submit' | 'post' | 'reverse' | null>(null);
  const [reverseDate, setReverseDate] = useState(() =>
    businessToday(session.data?.businessTimezone),
  );
  const [reason, setReason] = useState('');
  const detail = useQuery({
    queryKey: ['journal', id],
    queryFn: () => getData(`${JOURNALS}/${id}`, JournalDetailSchema),
  });
  const refresh = async () => {
    await refreshAfterPosting(queryClient);
    await queryClient.invalidateQueries({ queryKey: workflowHistoryKey(JOURNAL, id) });
    await queryClient.invalidateQueries({ queryKey: ['audit'] });
    await queryClient.invalidateQueries({ queryKey: ['report'] });
  };
  const act = useMutation({
    mutationFn: async (input: { kind: 'submit' | 'post' | 'reverse'; current: JournalDetailDto }) =>
      postJson(
        `${JOURNALS}/${input.current.journal.id}/${input.kind}`,
        input.kind === 'reverse' ? { postingDate: reverseDate, reason: reason.trim() } : {},
        JournalDetailSchema,
        session.data?.csrfToken ?? '',
        input.kind === 'reverse' ? {} : { expectedVersion: input.current.journal.version },
      ),
    onSuccess: async (result, input) => {
      setDialog(null);
      setReason('');
      await refresh();
      if (input.kind === 'reverse') await navigate(`/journals/${result.journal.id}`);
    },
  });
  const back = (
    <Button component={RouterLink} to="/journals" startIcon={<ArrowBackIcon />}>
      All journals
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the journal" />;
  if (detail.isError) {
    return <RecordFailure title="Journal" noun="journal" error={detail.error} back={back} />;
  }
  const { journal, source, original, related, lines } = detail.data;
  const route = source === null ? null : recordPath(source.recordType, source.recordId);
  const sourceText =
    source === null ? 'None' : `${source.displayName} ${source.summary ?? '(not visible)'}`;
  const balanced = journal.totalDebit === journal.totalCredit;
  const manual = journal.sourceType === 'manual';
  const draft = manual && journal.status === 'draft';
  const controlled = journal.journalKind === 'controlled_adjustment';
  const mayPrepare =
    can(`${JOURNAL}:create`) && (!controlled || can('ledger.controlled_adjustment:create'));
  const mayPost =
    can(`${JOURNAL}:post`) && (!controlled || can('ledger.controlled_adjustment:post'));
  const editable =
    draft && ['not_submitted', 'returned', 'rejected'].includes(journal.approvalStatus);
  const reversed = related.some((r) => r.journalKind === 'reversal');
  const current = detail.data;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Journal ${journal.documentNo}`}
        description={`${label(journal.sourceType)} · ${label(journal.journalKind)}`}
        actions={
          <>
            {back}
            {editable && mayPrepare && (
              <Button component={RouterLink} to={`/journals/${journal.id}/edit`} variant="outlined">
                Edit
              </Button>
            )}
            {editable && mayPrepare && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('submit');
                }}
              >
                Submit for approval
              </Button>
            )}
            {draft && mayPost && journal.approvalStatus !== 'submitted' && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('post');
                }}
              >
                Post
              </Button>
            )}
            {manual &&
              journal.status === 'posted' &&
              journal.journalKind !== 'reversal' &&
              !reversed &&
              mayPost && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('reverse');
                  }}
                >
                  Reverse
                </Button>
              )}
          </>
        }
      />
      <Section title="Journal">
        <Facts
          label="Journal details"
          facts={[
            ['Status', <StatusChip key="s" status={journal.status} />],
            ...(manual
              ? ([['Approval', <StatusChip key="a" status={journal.approvalStatus} />]] as [
                  string,
                  ReactNode,
                ][])
              : []),
            ['Posting date', journal.postingDate],
            ['Accounting period', journal.accountingPeriodCode],
            [
              'Source type',
              journal.sourceType === 'opening_balance' ? (
                <span key="st">
                  {label(journal.sourceType)} ·{' '}
                  <RecordLink
                    to={`/reports/opening-balance-reconciliation?journalId=${journal.id}`}
                  >
                    sub-ledger reconciliation
                  </RecordLink>
                </span>
              ) : (
                label(journal.sourceType)
              ),
            ],
            ['Kind', label(journal.journalKind)],
            [
              'Source transaction',
              route === null || source === null || source.summary === null ? (
                sourceText
              ) : (
                <RecordLink key="src" to={route}>
                  {sourceText}
                </RecordLink>
              ),
            ],
            ['Reference', journal.reference ?? '—'],
            ['Description', journal.description ?? '—'],
            ...(journal.adjustmentReason === null
              ? []
              : ([['Adjustment reason', journal.adjustmentReason]] as [string, string][])),
            [
              'Reverses / adjusts',
              original === null ? (
                '—'
              ) : (
                <RecordLink key="o" to={`/journals/${original.id}`}>
                  {original.documentNo}
                </RecordLink>
              ),
            ],
            [
              'Reversed / adjusted by',
              related.length === 0
                ? '—'
                : related.map((r) => (
                    <Link
                      key={r.id}
                      component={RouterLink}
                      to={`/journals/${r.id}`}
                      sx={{ mr: 1, fontWeight: 600 }}
                    >
                      {r.documentNo} ({label(r.journalKind)})
                    </Link>
                  )),
            ],
            ['Posted at', journal.postedAt ?? '—'],
          ]}
        />
      </Section>
      <Section title="Lines">
        <Table size="small" aria-label="Journal lines">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Cost centre</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Client / project / asset</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {can('ledger.account:view') ? (
                    <RecordLink to={`/accounts/${line.accountId}`}>
                      {line.accountCode} — {line.accountName}
                    </RecordLink>
                  ) : (
                    `${line.accountCode} — ${line.accountName}`
                  )}
                </TableCell>
                <TableCell>{line.description ?? '—'}</TableCell>
                <TableCell align="right">{line.debit === '0.00' ? '' : line.debit}</TableCell>
                <TableCell align="right">{line.credit === '0.00' ? '' : line.credit}</TableCell>
                <TableCell>
                  {line.supplierId === null ? '—' : (line.supplierSummary ?? 'Not visible')}
                </TableCell>
                <TableCell>{names.costCentre(line.costCenterId)}</TableCell>
                <TableCell>{names.employee(line.employeeId)}</TableCell>
                <TableCell>{names.location(line.locationId)}</TableCell>
                <TableCell>
                  {line.clientId === null && line.projectId === null && line.assetId === null
                    ? '—'
                    : null}
                  {line.clientId !== null && (
                    <div>
                      <DimensionRef kind="client" id={line.clientId} />
                    </div>
                  )}
                  {line.projectId !== null && (
                    <div>
                      <DimensionRef kind="project" id={line.projectId} />
                    </div>
                  )}
                  {line.assetId !== null && (
                    <div>
                      <DimensionRef kind="asset" id={line.assetId} />
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} component="th" scope="row" sx={{ fontWeight: 700 }}>
                Total {balanced ? '(balanced)' : '(NOT BALANCED)'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {journal.totalDebit}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {journal.totalCredit}
              </TableCell>
              <TableCell colSpan={5} />
            </TableRow>
          </TableBody>
        </Table>
      </Section>
      {manual && (
        <ApprovalPanel
          recordType={JOURNAL}
          recordId={journal.id}
          onDecided={() => void refresh()}
        />
      )}
      <DocumentsPanel
        recordType={JOURNAL}
        recordId={journal.id}
        canAttach={can(`${JOURNAL}:create`) || can(`${JOURNAL}:post`)}
        title="Supporting documents"
      />
      <RecordHistory recordType={JOURNAL} recordId={journal.id} />
      {(dialog === 'submit' || dialog === 'post') && (
        <ConfirmDialog
          open
          title={
            dialog === 'submit' ? `Submit ${journal.documentNo}?` : `Post ${journal.documentNo}?`
          }
          confirmLabel={dialog === 'submit' ? 'Submit' : 'Post'}
          busy={act.isPending}
          error={act.isError ? act.error.message : null}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            act.mutate({ kind: dialog, current });
          }}
          message={
            dialog === 'submit'
              ? 'The journal goes to the configured approval route and cannot be edited while it waits.'
              : 'Posting checks every account and dimension rule and updates the ledger. A posted journal is never edited; it is corrected by reversal.'
          }
        />
      )}
      {dialog === 'reverse' && (
        <FormDialog
          open
          title={`Reverse ${journal.documentNo}`}
          submitLabel="Post reversal"
          canSubmit={reason.trim() !== '' && reverseDate !== ''}
          submitting={act.isPending}
          error={act.isError ? act.error.message : null}
          onClose={() => {
            setDialog(null);
          }}
          onSubmit={() => {
            act.mutate({ kind: 'reverse', current });
          }}
        >
          <TextField
            type="date"
            label="Reversal posting date"
            required
            value={reverseDate}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              setReverseDate(e.target.value);
            }}
          />
          <TextField
            label="Reason"
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </FormDialog>
      )}
    </Stack>
  );
}
